import {
    BUILD_TIMESTAMP,
    BUILD_VERSION,
    GATE_COVER_PANEL_POSITION,
    GATE_TYPE,
    PANEL_TYPE, BLINK_SDK_READY_EVENT, BLINK_ACTION_URL_PARAM, BLINK_SDK_SETUP_EVENT
} from "./Constants";
import {evaluatePanelOptions} from "./PanelOptionsLogic";
import {IFrameMessages} from "./messaging/IFrameMessages";
import {formatAgoMessage, getClientIdFromCurrentScriptUrl} from "./utils/Utils";
import {generateUniqueId, getQueryParam, isPlainObject, NOOP_FUNCTION} from "../blinkpay/UtilsLib";
import {dispatchAnalyticsEvent, AnalyticsEventType} from "./utils/AnalyticsClient";
import {userDataModule} from "./modules/UserDataModule";
import {iframeToSdkChannel} from "./messaging/IframeToSdkChannel";
import {uxFlowModule} from "./modules/UXFlowModule";
import {purchaseModule} from "./modules/PurchaseModule";
import {analyticsModule} from "./modules/AnalyticsModule";
import {sdkState} from "./modules/SDKState";
import {panelModule} from "./modules/PanelModule";
import {walletModule} from "./modules/WalletModule";
import {userJourneyModule} from "./modules/user-journey/UserJourneyModule";
import {merchantFunctionModule} from "./modules/merchant-state/MerchantFunctionModule";
import {merchantVariableModule} from "./modules/merchant-state/MerchantVariableModule";
import {StorageScope} from "./modules/merchant-state/MerchantStorage";
import {fetchPublicData, publicDataLoaded, getDefaultSDKOptions, getPublicDataStore} from "./modules/PublicData";
import {facebookPixelModule} from "./modules/FacebookPixel";
import {getPageLoadTimings, markPageLoadTiming, PAGE_LOAD_TIMING_MARKS} from "./utils/pageLoadTiming";
import {widgetLoadedDispatcher} from "./messaging/SdkToIFrameChannel";
import {runBlinkAction} from "./modules/user-journey/engine/JourneyActionRunner";
import {onboardingModule} from "./modules/OnboardingModule";
import {evaluateConditionAndRun} from "./modules/user-journey/engine/JourneyEvaluator";
import {initMerchantBuiltinFunctions} from "./modules/merchant-state/initMerchantBuiltinFunctions.js";


function normalizeOptionsOrPanelId(optionsOrPanelId) {
    if (!optionsOrPanelId) {
        return {};
    }
    if (!isPlainObject(optionsOrPanelId)) {
        return {panel: optionsOrPanelId};
    }

    // For backwards compatibility, allow multiple keys
    optionsOrPanelId.panel = optionsOrPanelId.panel || optionsOrPanelId.pgId || optionsOrPanelId.panelId || optionsOrPanelId.panelAlias;

    return optionsOrPanelId;
}

let widgetVersion = null;
const sdkVersion = {
    build: BUILD_VERSION,
    time: new Date(BUILD_TIMESTAMP),
};
let initOptions = null;
let isInitCalled = false;
let isInitFinished = false;

class BlinkSDK {
    constructor() {
        initMerchantBuiltinFunctions();

        analyticsModule.init();
        userDataModule.init();
        purchaseModule.init();
        walletModule.init();
        panelModule.init();
        uxFlowModule.init();
        onboardingModule.init();

        iframeToSdkChannel.addListener(IFrameMessages.WIDGET_DATA_LOADED, () => {
            dispatchAnalyticsEvent(AnalyticsEventType.PAGE_VIEW, getPageLoadTimings());

            window.dispatchEvent(new CustomEvent(BLINK_SDK_SETUP_EVENT, {}));

            if (initOptions.journeys) {
                userJourneyModule.init(initOptions.journeys);
            }
            panelModule.resolvePendingRequests();
            purchaseModule.resolvePendingRequests();

            runBlinkAction(getQueryParam(BLINK_ACTION_URL_PARAM));

            window.dispatchEvent(new CustomEvent(BLINK_SDK_READY_EVENT, {}));

            isInitFinished = true;
        });

        const clientId = getClientIdFromCurrentScriptUrl();
        if (clientId) {
            this.init({clientId});
        }
    }

    doInit(givenOptions) {
        initOptions = {
            // Including here all options with a non-null default.
            storeAnalyticsCookies: true,
            ...getDefaultSDKOptions(),
            ...givenOptions,
        };

        if (initOptions.allowUserToCoverFees) {
            sdkState.update({allowUserToCoverFees: initOptions.allowUserToCoverFees});
        }

        if (initOptions.variables) {
            merchantVariableModule.add(initOptions.variables);
        }

        analyticsModule.setEnableStorage(!!initOptions.storeAnalyticsCookies);

        sdkState.update({
            hideWallet: !!initOptions.hideWallet,
        });
        walletModule.renderWallet(initOptions.widgetPositionYOffset);

        facebookPixelModule.init();

        // TODO @branch Remove this.
        window.dispatchEvent(new CustomEvent("blinkPaywallInitialized", {}));

        markPageLoadTiming(PAGE_LOAD_TIMING_MARKS.sdkInit);
    }

    // PUBLIC API STARTS HERE

    // Useful constants for the createPanel function
    PANEL_TYPE = PANEL_TYPE;
    GATE_TYPE = GATE_TYPE;
    GATE_COVER_PANEL_POSITION = GATE_COVER_PANEL_POSITION;

    async init(options) {
        if (isInitCalled) {
            return;
        }
        isInitCalled = true;

        // This is required in order to be backwards compatible with the deprecated setOptions() API
        // TODO: Keep an array of deprecated options (and their renaming counterparts).
        fetchPublicData(options.clientId || options.publisherDomainId);

        widgetLoadedDispatcher.addListener((version) => {
            // TODO @branch this logic probably belongs right next to where we first call widgetLoadedDispatcher
            widgetVersion = version;
            markPageLoadTiming(PAGE_LOAD_TIMING_MARKS.iFrameJS);
        });

        await Promise.all([widgetLoadedDispatcher.awaitOnce(), publicDataLoaded.awaitOnce()]);
        this.doInit(options);
    }

    setOptions(options) {
        if (!isInitCalled) {
            this.init(options);
        } else {
            sdkState.update(options);
        }
    }

    requestPayment(request, callback = NOOP_FUNCTION, errorCallback = NOOP_FUNCTION) {
        purchaseModule.requestPayment(request, callback, errorCallback);
    }

    createPanel(nodesOrSelector, optionsOrPanelId) {
        panelModule.createPanel(nodesOrSelector, normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    createPopup(optionsOrPanelId) {
        panelModule.createPopup(normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    // Flows

    promptManageAccount(optionsOrPanelId) {
        panelModule.createPopup({...normalizeOptionsOrPanelId(optionsOrPanelId), type: PANEL_TYPE.dashboard});
    }

    promptAuthPopup(optionsOrPanelId) {
        panelModule.createAuthenticationPopup(normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    promptSubscriptionPopup(optionsOrPanelId) {
        uxFlowModule.enterFlow(PANEL_TYPE.subscribe, normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    promptGiftSubscriptionPopup(optionsOrPanelId) {
        uxFlowModule.enterFlow(PANEL_TYPE.giftSubscription, normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    promptDonationPopup(optionsOrPanelId) {
        uxFlowModule.enterFlow(PANEL_TYPE.donation, {
            ...normalizeOptionsOrPanelId(optionsOrPanelId),
            skipCTA: true,
        });
    }

    promptShopPopup(optionsOrPanelId) {
        uxFlowModule.enterFlow(PANEL_TYPE.shop, normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    showDonationModal(optionsOrPanelId) {
        uxFlowModule.enterFlow(PANEL_TYPE.donation, normalizeOptionsOrPanelId(optionsOrPanelId));
    }

    promptNewsletterPopup(optionsOrPanelId) {
        panelModule.createPopup({
            ...normalizeOptionsOrPanelId(optionsOrPanelId),
            type: PANEL_TYPE.newsletter,
            skipCTA: true,
        });
    }

    // Authentication and user data

    getUserData() {
        return userDataModule.userData;
    }

    isAuthenticated() {
        return userDataModule.isAuthenticated();
    }

    isSubscribed() {
        return userDataModule.isSubscriber();
    }

    getSubscription() {
        return userDataModule.userData.subscription;
    }

    getStore(objectType) {
        return getPublicDataStore(objectType);
    }

    onAuthenticationChange(callback, runAtStart = false) {
        // TODO: hack
        const callbackWrapper = () => callback({authenticated: this.isAuthenticated()});
        userDataModule.authenticationChange.addListener(callbackWrapper);
        if (runAtStart) {
            if (isInitFinished) {
                callbackWrapper();
            } else {
                const handler = () => {
                    callbackWrapper();
                    window.removeEventListener(BLINK_SDK_READY_EVENT, handler);
                };
                window.addEventListener(BLINK_SDK_READY_EVENT, handler);
            }
        }
    }

    onSubscriptionChange(callback) {
        userDataModule.subscriptionChange.addListener(() => callback(userDataModule.userData.subscription));
    }

    // Short hand functions for updating options

    setEnableAnalyticsStorage(value) {
        analyticsModule.setEnableStorage(!!value);
    }

    showWallet() {
        this.setOptions({hideWallet: false});
    }

    hideWallet() {
        this.setOptions({hideWallet: true});
    }

    // Merchant variables

    hasVariable(key) {
        return merchantVariableModule.has(key);
    }

    getVariable(key) {
        return merchantVariableModule.get(key);
    }

    setVariable(key, value, scope=StorageScope.MEMORY) {
        return merchantVariableModule.set(key, value, scope)
    }

    evaluatePanelOptions(aliasOrId) {
        return evaluatePanelOptions({panel: aliasOrId});
    }

    // TODO @cleanup deprecate this in TNR code
    getPanelOptions(aliasOrId) {
        return this.evaluatePanelOptions(aliasOrId);
    }

    allVariables() {
        return merchantVariableModule.all();
    }

    // Merchant functions

    hasFunction(key) {
        return !!merchantFunctionModule.get(key);
    }

    getFunction(key) {
        return merchantFunctionModule.get(key);
    }

    addFunction(key, func) {
        return merchantFunctionModule.add({[key]: func});
    }

    addFunctions(functions) {
        return merchantFunctionModule.add(functions);
    }

    callFunction(key, ...args) {
        return merchantFunctionModule.call(key, ...args);
    }

    allFunctions() {
        return merchantFunctionModule.all();
    }

    getExceptionHandler() {
        return merchantFunctionModule.exceptionHandler;
    }

    setExceptionHandler(exceptionHandler) {
        const oldExceptionHandler = merchantFunctionModule.exceptionHandler;
        merchantFunctionModule.exceptionHandler = exceptionHandler;
        return oldExceptionHandler;
    }

    // Analytics

    sendAnalyticsEvent(payload) {
        dispatchAnalyticsEvent(AnalyticsEventType.CUSTOM, payload);
    }

    addAnalyticsEventListener(type, callback) {
        const safeCallback = (...args) => {
            try {
                callback(...args);
            } catch (e) {
                console.error(e);
            }
        };
        if (type === "*") {
            return analyticsModule.allEventsDispatcher.addListener(safeCallback);
        } else {
            return analyticsModule.eventsByTypeDispatcher.addListener(type, safeCallback);
        }
    }

    // Build version

    getBuildVersion() {
        return {
            sdkVersion,
            widgetVersion,
        };
    }

    printBuildVersion() {
        const buildVersion = this.getBuildVersion();
        const printInfo = (label, version) => {
            console.group(label);
            console.log("build: ", version.build);
            console.log("time: ", version.time);
            console.log("age: ", formatAgoMessage(version.time));
            console.groupEnd();
        };
        printInfo("Blink SDK", buildVersion.sdkVersion);
        printInfo("Iframe", buildVersion.widgetVersion);
    }

    // Journeys
    runAction(blinkAction) {
        runBlinkAction(blinkAction);
    }

    // A function for testing journey options (usually meant to be called in the
    // console or in automated tests, not in production).
    // Returns an object containing an auto-generated ID and alias for the mock
    // journey, and a cleanup function that can be called to close the execution
    // loop of the journey.
    testJourneyOptions(options) {
        const id = "test_journey_id_" + generateUniqueId().replace(/-/g, "_");
        const alias = "test_journey_alias_" + generateUniqueId().replace(/-/g, "_");
        const journey = {id, alias, options};
        const evaluator = evaluateConditionAndRun(journey);
        return {
            id,
            alias,
            cleanup: () => evaluator?.cleanup(),
        };
    }

    // Deprecated API

    // Indicates if doInit(options) was called successfully
    isInitialized() {
        return isInitFinished;
    }

    onSubscriptionStatusChange(callback) {
        userDataModule.subscriptionChange.addListener(() => {
            callback({subscribed: !!userDataModule.userData.subscription});
        });
    }

    createPG(nodesOrSelector, panelOptions, gateOptions=null, hooks=null) {
        if (!isPlainObject(panelOptions)) {
            panelOptions = {type: panelOptions};
        }
        panelModule.createPanel(nodesOrSelector, {
            ...hooks,
            ...panelOptions,
            gateType: panelOptions.gateType || (isPlainObject(gateOptions) ? gateOptions.type : gateOptions) || null,
        });
    }
}

export default new BlinkSDK();
